(() => {
  // Only define CartDrawer if it hasn't been defined yet
  if (!customElements.get("cart-drawer")) {
    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
        
        // Use event delegation for overlay clicks to handle dynamic content
        this.addEventListener("click", (event) => {
          if (event.target && event.target.id === "CartDrawer-Overlay") {
            this.close();
          }
        });
        
        this.setHeaderCartIconAccessibility();
        this.bindUpsellButton();
        
        // Additional fallback: ensure overlay click always works
        this.ensureOverlayClickHandler();
      }

      ensureOverlayClickHandler() {
        // Use event delegation on document body as a fallback
        document.body.addEventListener("click", (event) => {
          if (event.target && event.target.id === "CartDrawer-Overlay" && this.classList.contains("active")) {
            event.preventDefault();
            event.stopPropagation();
            this.close();
          }
        });
        
        // Fix overlay pointer events when cart drawer is active
        this.addEventListener("classchange", () => {
          this.fixOverlayPointerEvents();
        });
        
        // Watch for class changes using MutationObserver
        const observer = new MutationObserver(() => {
          this.fixOverlayPointerEvents();
        });
        
        observer.observe(this, { attributes: true, attributeFilter: ['class'] });
      }

      fixOverlayPointerEvents() {
        const overlay = this.querySelector("#CartDrawer-Overlay");
        if (overlay) {
          const isActive = this.classList.contains("active");
          const isCustomizationMode = this.classList.contains("customization-mode");
          
          // Only allow pointer events when drawer is active and not in customization mode
          if (isActive && !isCustomizationMode) {
            overlay.style.pointerEvents = "auto";
            overlay.style.cursor = "pointer";
          } else if (isCustomizationMode) {
            overlay.style.pointerEvents = "none";
          }
        }
      }

      setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector("#cart-icon-bubble");
        if (!cartLink) return;

        cartLink.setAttribute("role", "button");
        cartLink.setAttribute("aria-haspopup", "dialog");

        cartLink.addEventListener("click", (event) => {
          event.preventDefault();
          this.open(cartLink);
        });

        cartLink.addEventListener("keydown", (event) => {
          if (event.code.toUpperCase() === "SPACE") {
            event.preventDefault();
            this.open(cartLink);
          }
        });
      }

      bindUpsellButton() {
        this.addEventListener("click", async (event) => {
          const upsellButton = event.target.closest(".upsell-add-button");
          if (upsellButton) {
            event.preventDefault();
            
            // Prevent multiple clicks
            if (upsellButton.disabled || upsellButton.classList.contains('loading')) {
              return;
            }
            
            const form = upsellButton.closest(".upsell-form");
            const variantId = form.querySelector('input[name="id"]').value;
            
            await this.addItemToCart(variantId, upsellButton);
          }
        });
      }

      async addItemToCart(variantId, buttonElement = null) {
        let originalText = '';
        
        // Set loading state if button provided
        if (buttonElement) {
          buttonElement.disabled = true;
          buttonElement.classList.add('loading');
          originalText = buttonElement.querySelector('.upsell-button-text') ? 
            buttonElement.querySelector('.upsell-button-text').textContent : 
            buttonElement.textContent;
        }
        
        const sections = this.getSectionsToRender().map((s) => s.id);
        const body = JSON.stringify({
          id: variantId,
          quantity: 1,
          sections,
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(`${routes.cart_add_url}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body,
          });

          if (response.status === 422) {
            const errorData = await response.json();
            
            if (buttonElement) {
              // Show error feedback briefly
              const textElement = buttonElement.querySelector('.upsell-button-text') || buttonElement;
              textElement.textContent = 'Error';
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
            return;
          }

          const parsedState = await response.json();
          await this.renderContents(parsedState);
          
          if (buttonElement) {
            // Show success feedback briefly
            const textElement = buttonElement.querySelector('.upsell-button-text') || buttonElement;
            textElement.textContent = 'Added!';
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
        } catch (error) {
          
          if (buttonElement) {
            // Show error feedback briefly
            const textElement = buttonElement.querySelector('.upsell-button-text') || buttonElement;
            textElement.textContent = 'Error';
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } finally {
          if (buttonElement) {
            // Always reset loading state
            const textElement = buttonElement.querySelector('.upsell-button-text') || buttonElement;
            textElement.textContent = originalText;
            buttonElement.disabled = false;
            buttonElement.classList.remove('loading');
          }
        }
      }

      startCountdownTimer() {
        const timerElement = document.getElementById("cart-countdown-timer");
        if (!timerElement) return;

        let minutes = 10;
        let seconds = 0;

        if (this.countdownInterval) clearInterval(this.countdownInterval);

        const updateTimer = () => {
          timerElement.textContent = `${minutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`;
        };

        updateTimer();

        this.countdownInterval = setInterval(() => {
          if (seconds === 0) {
            if (minutes === 0) {
              clearInterval(this.countdownInterval);
              return;
            }
            minutes--;
            seconds = 59;
          } else {
            seconds--;
          }
          updateTimer();
        }, 1000);
      }

      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);

        setTimeout(() => {
          this.classList.add("animate", "active");
          // Ensure overlay is clickable when drawer opens
          this.fixOverlayPointerEvents();
        });

        this.addEventListener(
          "transitionend",
          () => {
            const containerToTrapFocusOn = this.classList.contains("is-empty")
              ? this.querySelector(".drawer__inner-empty")
              : document.getElementById("CartDrawer");
            const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
            trapFocus(containerToTrapFocusOn, focusElement);
          },
          { once: true }
        );

        document.body.classList.add("overflow-hidden");

        if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
          const $slider = $(".gbfrequently-bought-with-main-whole-slider");
          if ($slider.length > 0 && !$slider.hasClass("slick-initialized")) {
            $slider.slick({
              infinite: true,
              autoplaySpeed: 1000,
              autoplay: false,
              slidesToShow: 1,
              slidesToScroll: 1,
              arrows: false,
              dots: true,
            });
            $slider.css("opacity", "1");
          }
        }

        this.startCountdownTimer();
      }

      close() {
        const cartCustomizationMode = this.classList.contains("customization-mode");
        if (cartCustomizationMode) {
          alert(
            "Cart Customization Mode is active. Disable it in Theme Settings > Cart Drawer to allow closing."
          );
          return;
        }

        this.classList.remove("active");
        removeTrapFocus(this.activeElement);
        document.body.classList.remove("overflow-hidden");

        if (this.countdownInterval) clearInterval(this.countdownInterval);
      }

      setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute("role", "button");
        cartDrawerNote.setAttribute("aria-expanded", "false");
        if (cartDrawerNote.nextElementSibling?.id) {
          cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
        }

        cartDrawerNote.addEventListener("click", (event) => {
          event.currentTarget.setAttribute(
            "aria-expanded",
            !event.currentTarget.closest("details").hasAttribute("open")
          );
        });

        cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
      }

      renderContents(parsedState, options = {}) {
        const shouldOpen = options.open !== false;
        const drawerInner = this.querySelector(".drawer__inner");
        if (drawerInner?.classList.contains("is-empty")) drawerInner.classList.remove("is-empty");
        this.productId = parsedState.id;

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement) return;

          if (section.id === "cart-drawer" || section.selector === "#CartDrawer") {
            const newCartDrawer = this.getSectionDOM(parsedState.sections[section.id], section.selector);
            if (newCartDrawer) {
              updateCartDrawer(sectionElement, newCartDrawer);
            }
          } else {
            const sectionHTML = parsedState.sections[section.id];
            if (sectionHTML) {
              sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
              
              // Fix cart icon visibility after HTML update
              if (section.id === "cart-icon-bubble") {
                const iconWrapper = sectionElement.querySelector(".svg-wrapper");
                if (iconWrapper) {
                  const bagIcon = iconWrapper.querySelector(".cart-icon-bag");
                  const cartIcon = iconWrapper.querySelector(".cart-icon-cart");
                  const cartIconEmpty = iconWrapper.querySelector(".cart-icon-cart-empty");
                  
                  // Get current cart icon type from data attribute
                  const iconType = sectionElement.getAttribute("data-cart-icon-type") || "bag";
                  
                  // Hide all icons first
                  if (bagIcon) bagIcon.style.display = "none";
                  if (cartIcon) cartIcon.style.display = "none";
                  if (cartIconEmpty) cartIconEmpty.style.display = "none";
                  
                  // Show the correct icon based on type
                  if (iconType === "bag" && bagIcon) {
                    bagIcon.style.display = "flex";
                  } else if (iconType === "cart") {
                    const cartEmpty = parsedState.item_count === 0;
                    if (cartEmpty && cartIconEmpty) {
                      cartIconEmpty.style.display = "flex";
                    } else if (cartIcon) {
                      cartIcon.style.display = "flex";
                    }
                  }
                }
              }
            }
          }
        });

        setTimeout(() => {
          this.fixOverlayPointerEvents();
          if (shouldOpen) this.open();
        });
      }

      getSectionInnerHTML(html, selector = ".shopify-section") {
        const element = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
        return element ? element.innerHTML : "";
      }

      getSectionsToRender() {
        return [
          { id: "cart-drawer", selector: "#CartDrawer" },
          { id: "cart-icon-bubble" },
        ];
      }

      getSectionDOM(html, selector = ".shopify-section") {
        if (!html) return null;
        return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
      }

      setActiveElement(element) {
        this.activeElement = element;
      }
    }

    customElements.define("cart-drawer", CartDrawer);
  }

  // Define CartDrawerItems safely
  const defineCartDrawerItems = () => {
    if (customElements.get("cart-drawer-items")) return;

    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
        ];
      }
    }

    customElements.define("cart-drawer-items", CartDrawerItems);
  };

  if (typeof CartItems !== "undefined") {
    defineCartDrawerItems();
  } else {
    const checkCartItems = setInterval(() => {
      if (typeof CartItems !== "undefined") {
        clearInterval(checkCartItems);
        defineCartDrawerItems();
      }
    }, 50);
  }
})();
